"use client";

import { Briefcase, Zap, ChartBar as BarChart3, Flame, ChevronRight, Check, X } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
export default function OurStrategicPillars() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Briefcase,
    number: "01",
    title: "OTA Yield Optimization",
    question: "How do you maximize OTA revenue?",
    description: "We master global distribution channels with dynamic pricing algorithms that typically drive a 15-25% increase in OTA revenue while maintaining rate parity.",
    link: "/OTARevenueManagementListingsPage",
    ariaLabel: "OTA revenue management strategy dashboard"
  }, {
    icon: Zap,
    number: "02",
    title: "Tech Stack Architecture",
    question: "What is dynamic pricing strategy?",
    description: "We audit and deploy high-performance PMS and RMS integrations that automate demand forecasting, resulting in measurable ADR uplift and reduced manual overhead.",
    link: "/HotelTechStackConsultingPage",
    ariaLabel: "hospitality tech stack implementation"
  }, {
    icon: BarChart3,
    number: "03",
    title: "Precision Revenue Audits",
    question: "How do you optimize hotel occupancy?",
    description: "Our deep-dive financial forensics identify hidden leakage and untapped segments, often uncovering 10% or more in missed bottom-line profitability.",
    link: "/HotelRevenueAuditFreePaidPage",
    ariaLabel: "hotel revenue audit process"
  }, {
    icon: Flame,
    number: "04",
    title: "Predictive Market Intelligence",
    question: "How do you leverage real-time market data?",
    description: "By converting real-time data into aggressive growth strategies, we ensure your property consistently outperforms the comp-set in RevPAR and market share.",
    link: "/DynamicPricingForecastingServicesPage",
    ariaLabel: "dynamic pricing algorithm visualization"
  }];
  const stats = [{
    number: "24/7",
    unit: "",
    label: "Market Monitoring"
  }, {
    number: "100%",
    unit: "",
    label: "Data-Driven Focus"
  }, {
    number: "4",
    unit: "",
    label: "Core Pillars"
  }];
  const comparisonData = [{
    category: "OTA & Listings",
    features: [{
      name: "Rate Parity Monitoring",
      baba: "24/7 Automated",
      comp: "Manual Updates",
      link: "/RateParityLeakageControl"
    }, {
      name: "Content Score Optimization",
      baba: "95% Minimum",
      comp: "Basic Setup",
      link: "/OTAListingOptimizationServices2"
    }, {
      name: "Review Management",
      baba: "AI-Assisted Response",
      comp: "Owner Managed",
      link: "/RateParityLeakageControl"
    }]
  }, {
    category: "Website & SEO",
    features: [{
      name: "Direct Booking Engine",
      baba: "High-Conversion UI",
      comp: "Standard Template",
      link: "/HotelWebsiteSEOBookingEngineOptimization"
    }, {
      name: "SEO Strategy",
      baba: "AEO & GEO Optimized",
      comp: "Basic Keywords",
      link: "/HotelWebsiteSEOBookingEngineOptimization"
    }, {
      name: "Page Load Speed",
      baba: "< 2 Seconds",
      comp: "4-6 Seconds",
      link: "/HotelWebsiteSEOBookingEngineOptimization"
    }]
  }, {
    category: "Revenue Management",
    features: [{
      name: "Pricing Strategy",
      baba: "Dynamic Hourly",
      comp: "Static Daily",
      link: "/HotelRevenueManagementConsulting"
    }, {
      name: "Demand Forecasting",
      baba: "Predictive AI",
      comp: "Historical Only",
      link: "/DynamicPricingForecastingServices2"
    }, {
      name: "Market Share Analysis",
      baba: "Real-time CompSet",
      link: "/RevenueIntelligenceFrameworkOurMethodology"
    }]
  }, {
    category: "Tech Stack",
    features: [{
      name: "PMS Integration",
      baba: "Full Bi-Directional",
      comp: "Manual Entry",
      link: "/ToolExpertiseTechnologyStack"
    }, {
      name: "RMS Automation",
      baba: "Auto-Pilot Mode",
      comp: "Human Approval",
      link: "/ToolExpertiseTechnologyStack"
    }, {
      name: "Channel Manager",
      baba: "Instant Sync",
      comp: "Delayed Sync",
      link: "/ChannelManagerSetupDistributionStrategy"
    }]
  }, {
    category: "Competitor Comparisons",
    features: [{
      name: "Service Model",
      baba: "Dedicated Expert",
      comp: "Call Center",
      link: "/RevenueIntelligenceFrameworkOurMethodology"
    }, {
      name: "Contract Terms",
      baba: "Performance Based",
      comp: "Fixed Monthly",
      link: "/HotelRevenueManagementConsulting"
    }, {
      name: "Reporting",
      baba: "Live Dashboard",
      comp: "PDF Monthly",
      link: "/RevenueIntelligenceFrameworkOurMethodology"
    }]
  }];
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": `${typeof window !== 'undefined' ? window.location.origin : 'https://hotelrevbaba.com'}/`
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${typeof window !== 'undefined' ? window.location.origin : 'https://hotelrevbaba.com'}/services`
    }]
  };
  return <section data-section-id="153" id="our-strategic-pillars" className="bg-[#0B4D9C] text-white relative overflow-hidden py-24 md:py-32 lg:py-36">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(breadcrumbSchema)
    }} />
      
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 mb-12">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 mb-8">
          <Link to="/" className="hover:text-[#D4A574] transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-[#D4A574]">Services</span>
        </nav>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-left">Our Hotel Revenue Management Services</h1>
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[60%]" style={{
      background: "radial-gradient(ellipse 60% 50% at 50% 0%, hsla(43, 46%, 55%, 0.15), transparent 70%)"
    }} />

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(18)].map((_, i) => <span key={i} data-index={i} className="absolute rounded-lg" style={{
        width: `${2 + i % 3}px`,
        height: `${2 + i % 3}px`,
        backgroundColor: "hsla(43, 46%, 55%, 0.25)",
        top: `${i * 53 % 100}%`,
        left: `${i * 37 % 100}%`,
        animation: `floatDust ${12 + i % 8}s ease-in-out ${i * 0.4}s infinite alternate`,
        filter: "blur(0.5px)"
      }} />)}
        <style>{`
          @keyframes floatDust {
            0%   { transform: translate(0, 0); opacity: 0.2; }
            50%  { transform: translate(20px, -30px); opacity: 0.6; }
            100% { transform: translate(-15px, -60px); opacity: 0.1; }
          }
          @keyframes fadeUpStep {
            0%   { opacity: 0; transform: translateY(24px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[40fr_60fr] lg:gap-20">
          <div ref={leftRef} className={`lg:sticky lg:top-24 lg:self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-4">
              <span aria-hidden="true" className="block h-px w-12 bg-[#D4A574]" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
                Our Expertise
              </span>
            </div>

            <h2 className="mt-8 font-sans font-bold text-white leading-[1.1] tracking-tight" style={{
            fontSize: "36px"
          }}>
              Precision strategies for <em className="italic font-normal text-[#D4A574]">maximum</em> hotel revenue.
            </h2>

            <p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-white/80 font-sans">
              HotelRevBaBa provides a comprehensive suite of revenue solutions, from freelance management and tech stack audits to OTA optimization and direct booking growth.
            </p>

            <div className="mt-12">
              <Tabs defaultValue="OTA & Listings" className="w-full">
                <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0 mb-8 justify-start">
                  {comparisonData.map((tab, i) => <TabsTrigger key={i} value={tab.category} className="rounded-lg border border-white/10 bg-white/5 text-white/60 data-[state=active]:bg-[#D4A574] data-[state=active]:text-white px-4 py-2 text-xs font-mono uppercase tracking-wider transition-all">
                      {tab.category}
                    </TabsTrigger>)}
                </TabsList>

                {comparisonData.map((tab, i) => <TabsContent key={i} value={tab.category} className="mt-0 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <div className="overflow-hidden rounded-lg border border-white/10 bg-white/5">
                      <table className="hidden md:table w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-white/10 bg-white/5">
                            <th className="p-4 text-xs font-mono uppercase text-white/60">Feature</th>
                            <th className="p-4 text-xs font-mono uppercase text-[#D4A574]">HotelRevBaBa</th>
                            <th className="p-4 text-xs font-mono uppercase text-white/60">Competitors</th>
                            <th className="p-4 text-xs font-mono uppercase text-white/60">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tab.features.map((feature, idx) => <tr key={idx} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                              <td className="p-4 text-sm font-medium text-white">{feature.name}</td>
                              <td className="p-4 text-sm font-bold text-[#D4A574]">{feature.baba}</td>
                              <td className="p-4 text-sm text-white/60">{feature.comp}</td>
                              <td className="p-4">
                                <div className="flex gap-2">
                                  <Link to={feature.link}>
                                    <Button variant="ghost" size="sm" className="text-[10px] h-7 px-2 border border-white/10 hover:bg-[#D4A574]/10 hover:text-[#D4A574]">
                                      HotelRevBaBa optimizes OTA distribution
                                    </Button>
                                  </Link>
                                  <Link to="/ContactPage">
                                    <Button variant="secondary" size="sm" className="text-[10px] h-7 px-2 bg-[#D4A574] text-white hover:bg-[#D4A574]/90">
                                      Start
                                    </Button>
                                  </Link>
                                </div>
                              </td>
                            </tr>)}
                        </tbody>
                      </table>

                      <h3 className="md:hidden px-6 pt-6 text-lg font-semibold text-white">Key Features</h3>
                      <div className="md:hidden divide-y divide-white/10">
                        {tab.features.map((feature, idx) => <div key={idx} className="p-6 space-y-4">
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider">{feature.name}</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-[10px] font-mono text-[#D4A574] uppercase mb-1">HotelRevBaBa</p>
                                <p className="text-sm font-bold text-[#D4A574]">{feature.baba}</p>
                              </div>
                              <div>
                                <p className="text-[10px] font-mono text-white/40 uppercase mb-1">Competitors</p>
                                <p className="text-sm text-white/60">{feature.comp}</p>
                              </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                              <Link to={feature.link} className="flex-1">
                                <Button variant="outline" className="w-full text-xs border-white/20 text-white hover:bg-white/10">
                                  HotelRevBaBa optimizes OTA distribution
                                </Button>
                              </Link>
                              <Link to="/ContactPage" className="flex-1">
                                <Button className="w-full text-xs bg-[#D4A574] text-white hover:bg-[#D4A574]/90">
                                  Get Started
                                </Button>
                              </Link>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </TabsContent>)}
              </Tabs>
            </div>

            <div className="mt-10">
              <Link to="/ContactPage">
                <Button variant="outline" size="lg" className="rounded-lg border-[#D4A574] bg-transparent text-[#D4A574] hover:bg-[#D4A574]/10 hover:text-[#D4A574] px-6">
                  Book a Free Revenue Audit
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const jsonLd = {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": step.title,
              "description": step.description,
              "serviceProvider": {
                "@type": "LocalBusiness",
                "name": "HotelRevBaBa"
              },
              "audience": {
                "@type": "Audience",
                "audienceType": "Hotel owners, General Managers, and Investors in India"
              },
              "areaServed": "India"
            };
            return <div key={i} data-index={i} className="group flex flex-col items-start p-8 rounded-lg border border-white/10 bg-white/5" style={{
              animation: `fadeUpStep 700ms ease-out ${i * 120}ms both`
            }}>
                  <script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: JSON.stringify(jsonLd)
              }} />
                  
                  <div className="shrink-0 mb-6">
                    <Icon size={48} strokeWidth={1.25} style={{
                  color: "#D4A574"
                }} aria-label={step.ariaLabel} />
                  </div>

                  <div className="flex-1">
                    <div className="font-mono text-sm mb-4 text-[#D4A574]">
                      {step.number}
                    </div>
                    <h3 className="font-sans font-semibold text-xl leading-tight text-white mb-2">
                      {step.title}
                    </h3>
                    <h3 className="font-sans font-medium text-sm text-[#D4A574]/90 mb-4 italic">
                      {step.question}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/80 font-sans mb-4">
                      {step.description}{" "}
                      <Link to={step.link} className="text-[#D4A574] hover:underline font-medium">
                        Learn More
                      </Link>
                    </p>
                    <Link to={step.link} className="text-xs font-mono uppercase tracking-wider text-[#D4A574] group-hover:text-white transition-colors cursor-pointer" data-service={step.title}>
                      Explore {step.title} Strategy
                    </Link>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 border-t border-white/20 pt-12 md:pt-16">
          {stats.map((stat, i) => <div key={i} data-index={i} className={`flex flex-col items-center md:items-start px-4 md:px-10 py-6 md:py-0 ${i !== 0 ? "md:border-l border-white/20" : ""} ${i !== stats.length - 1 ? "border-b md:border-b-0 border-white/20" : ""}`}>
              <div className="flex items-baseline gap-2">
                <span className="font-serif font-light leading-none text-[#D4A574]" style={{
              fontSize: "clamp(3rem, 6vw, 4.5rem)"
            }}>
                  {stat.number}
                </span>
                {stat.unit && <span className="font-serif text-xl md:text-2xl text-[#D4A574]">
                    {stat.unit}
                  </span>}
              </div>
              <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-white/70 text-center md:text-left">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}